import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../redux/store/store';
import {
  useGetUserQuery,
  useGetOrganizationsQuery,
  useUpdateUserMutation,
} from '../../../redux/api';
import type { User } from '../../../redux/api';
import { Modal } from '../../../components/Modal';
import { Field } from '../../../components/Field';
import { AccountDeletionPanel } from '../../../components/AccountDeletionPanel';
import { IconEdit } from '../../../components/icons';
import { validatePhone } from '../../../utils/validation';
import { PhoneInput } from '../../../components/PhoneInput';
import { userStatusBadge } from '../../../config/status';
import { currentUserId } from '../../../utils/auth';

const initials = (name?: string | null) =>
  (name ?? '')
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

const statusTone = userStatusBadge;

type ProfileForm = {
  prefix: string;
  firstName: string;
  lastName: string;
  phone: string;
};

const formFromUser = (user?: User): ProfileForm => ({
  prefix: user?.prefix ?? '',
  firstName: user?.firstName ?? '',
  lastName: user?.lastName ?? '',
  phone: user?.phone ?? '',
});

// Profile. Shows the signed-in user (from layout state) enriched with the full
// record from the `services` GraphQL API, fetched by id, and lets them update
// their own details via `updateUser`. Available to every role — the `user(id)`
// query and self `updateUser` are allowed for one's own record, unlike the
// owner/admin-only `users` list.
const Profile = () => {
  const user = useSelector((s: RootState) => s.layout.user);
  const myId = currentUserId();
  const { data: me } = useGetUserQuery(myId ?? '', {
    skip: !myId,
  });
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const [updateUser, { isLoading: saving }] = useUpdateUserMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<ProfileForm>(formFromUser(me));
  const [formError, setFormError] = useState<string | null>(null);
  // Per-field validation messages — shown directly under the offending input
  // (with a red border) instead of the single top-of-form banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  // Keep the form in sync once the matching user record loads.
  useEffect(() => {
    if (!open) setForm(formFromUser(me));
  }, [me, open]);

  const setField = (key: keyof ProfileForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const openEdit = () => {
    setForm(formFromUser(me));
    setFormError(null);
    setFieldErrors({});
    setOpen(true);
  };

  const handleSave = async () => {
    if (!me) return;
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.firstName.trim()) errors.firstName = 'First name is required.';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required.';

    const phoneError = validatePhone(form.phone);
    if (phoneError) errors.phone = phoneError;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      await updateUser({
        id: me.id,
        input: {
          prefix: form.prefix.trim() || undefined,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim() || undefined,
          // Account status is an administrative field — not self-editable.
        },
      }).unwrap();
      setOpen(false);
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? 'Failed to update profile.'
      );
    }
  };

  const orgName = (id: string) =>
    organizations.find((org) => org.id === id)?.name ?? id;

  const displayName =
    (me ? `${me.firstName ?? ''} ${me.lastName ?? ''}`.trim() : '') || user.name;

  const memberships =
    me && me.organizations ? Object.entries(me.organizations) : [];

  const baseline = useMemo(() => formFromUser(me), [me]);
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Profile</h2>
          </div>
          <div className="toolbar">
            <button
              type="button"
              className="btn btn--primary"
              onClick={openEdit}
              disabled={!me}
              title={me ? undefined : 'No matching services user to edit.'}
            >
              <IconEdit /> Edit profile
            </button>
          </div>
        </div>

        <div className="profile-header">
          <span className="profile-avatar">{initials(displayName)}</span>
          <div className="profile-id">
            <h2>{displayName}</h2>
            <span className="profile-email">{user.email}</span>
            <div className="profile-badges">
              <span className="pill pill--neutral">{user.role}</span>
              {me?.status && (
                <span className={`badge ${statusTone(me.status)}`}>
                  <span className="badge__dot" />
                  {me.status}
                </span>
              )}
            </div>
          </div>
        </div>

        <dl className="profile-grid">
          <div>
            <dt>Prefix</dt>
            <dd>{me?.prefix ?? '—'}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{user.email}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{me?.phone ?? '—'}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>{user.role}</dd>
          </div>
          <div>
            <dt>Member since</dt>
            <dd>{formatDate(me?.createdAt)}</dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{formatDate(me?.updatedAt)}</dd>
          </div>
        </dl>
      </section>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Organizations</h2>
            <p className="panel__sub">{memberships.length} membership(s)</p>
          </div>
        </div>

        {memberships.length ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th><span className="th-sort" style={{ cursor: 'default' }}>Organization</span></th>
                  <th><span className="th-sort" style={{ cursor: 'default' }}>Roles</span></th>
                  <th><span className="th-sort" style={{ cursor: 'default' }}>Status</span></th>
                  <th><span className="th-sort" style={{ cursor: 'default' }}>Employee type</span></th>
                </tr>
              </thead>
              <tbody>
                {memberships.map(([orgId, membership]) => (
                  <tr key={orgId}>
                    <td className="cell-title">{orgName(orgId)}</td>
                    <td>
                      {(membership.role ?? []).map((role) => (
                        <span key={role} className="pill pill--neutral">
                          {role}
                        </span>
                      ))}
                    </td>
                    <td>
                      <span className={`badge ${statusTone(membership.status)}`}>
                        <span className="badge__dot" />
                        {membership.status ?? '—'}
                      </span>
                    </td>
                    <td>{membership.employeeType ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="profile-empty">
            No organization memberships
            {me ? '.' : ' — sign in as a services user to see memberships.'}
          </div>
        )}
      </section>

      <AccountDeletionPanel />


      <Modal
        open={open}
        title="Edit profile"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSave}
              disabled={saving || !dirty}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        {formError ? <div className="form-error">{formError}</div> : null}
        <div className="form-grid">
          <label className="field">
            Prefix
            <input
              value={form.prefix}
              onChange={(e) => setField('prefix', e.target.value)}
              placeholder="Mr / Ms / Dr"
            />
          </label>
          <label className="field">
            Status
            <span className="field__readonly">View only</span>
            <input
              value={
                me?.status
                  ? me.status.charAt(0).toUpperCase() + me.status.slice(1)
                  : '—'
              }
              disabled
              readOnly
              title="Account status is managed by an administrator."
            />
          </label>
          <Field label="First name *" error={fieldErrors.firstName}>
            <input
              value={form.firstName}
              onChange={(e) => setField('firstName', e.target.value)}
            />
          </Field>
          <Field label="Last name *" error={fieldErrors.lastName}>
            <input
              value={form.lastName}
              onChange={(e) => setField('lastName', e.target.value)}
            />
          </Field>
          <Field label="Phone" error={fieldErrors.phone} full>
            <PhoneInput
              value={form.phone}
              onChange={(v) => setField('phone', v)}
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
